import type { TextRun } from '@/api/types/stowed-element'
import { DEFAULT_POINT_SIZE } from '@/lib/text/constants/text-defaults'
import {
  MIN_LEADING_FRACTION_OF_NATURAL,
  hasExplicitLeading,
  naturalLeadingPt,
  parsedLeadingPt,
} from '@/lib/text/leading'
import type { ShapeTextFlow } from '../types/shape-text-flow'
import type { ShapeTextLayoutResult } from '../types/shape-text-layout-result'
import type { RowMetricPt } from '../types/row-metric-pt'

export const defaultRowMetricPt: RowMetricPt = (run) =>
  naturalLeadingPt(run?.pointSize ?? DEFAULT_POINT_SIZE)

/**
 * The TALLEST run on a row, by the height its text actually occupies — used for that row's own
 * glyph-box thickness (CSS `lineHeight`, caret height, selection-band height), so a taller run
 * sharing a row with smaller text never gets its ink clipped by too short a box. Same no-overlap
 * concern `rowMetricRun` (`shape-text-measure.ts`) exists for, scoped to one row instead of the
 * whole frame — and it must stay on the SAME metric as that one, or the frame-wide grid could be
 * shorter than a row it contains.
 *
 * Was point-size-only until WMPCM-9070 round 5. That silently assumed every face fits in
 * `NATURAL_LEADING_MULTIPLIER` em while the editor sizes its line boxes from the font's real
 * metrics, so a taller face overlapped the row below with no size or leading change involved — see
 * `runRowMetricPt`. Leading resolution remains a SEPARATE concern (`resolveRowLeadingRun`) and must
 * not be tied to whichever run happens to be tallest.
 */
export function resolveRowTallestRun(
  charIndices: number[],
  charRunMap: Array<TextRun | null>,
  rowMetricPt: RowMetricPt
): TextRun | undefined {
  let widest: TextRun | undefined
  let widestPt = -Infinity

  for (const i of charIndices) {
    const run = charRunMap[i]

    if (!run) continue
    const heightPt = rowMetricPt(run)

    if (heightPt > widestPt) {
      widestPt = heightPt
      widest = run
    }
  }

  return widest
}

/**
 * The run whose leading governs the gap from this row to the NEXT row.
 *
 * Prefers whichever run(s) on the row EXPLICITLY set a leading value over one still at its implicit
 * Metric default — picking the larger of two values only makes sense when BOTH are a real choice the
 * user made. Letting an untouched default silently outrank a smaller value the user just typed
 * defeats the point of setting it at all: selecting only part of a line and applying, say, Narrow
 * left the rest of that same row at the (numerically larger) Metric default, and a naive
 * "largest wins" rule — the right rule for `resolveRowTallestPointSizeRun`'s glyph-clipping concern —
 * made every value below the current default look like it had no effect at all.
 *
 * Among multiple EXPLICIT values on one row, the LAST one in reading order wins (the run closest to
 * the row's own end) — an arbitrary but necessary tie-break once there are two genuine choices to
 * pick between, favoring whichever was typed most recently in reading order.
 */
export function resolveRowLeadingRun(
  charIndices: number[],
  charRunMap: Array<TextRun | null>
): TextRun | undefined {
  let lastExplicit: TextRun | undefined
  let anyRun: TextRun | undefined

  for (const i of charIndices) {
    const run = charRunMap[i]

    if (!run) continue
    anyRun = run

    // One shared definition of "explicit", the same one `parsedLeadingPt` resolves against — see
    // `hasExplicitLeading`. This was tested by hand here, which missed `DEFAULT_LEADING` (`'auto'`):
    // `ensureTextRunDefaults` stamps that on every unleaded run, so after a single save every run in
    // the frame looked like a deliberate choice and the tie-break below became meaningless.
    if (hasExplicitLeading(run.leading)) lastExplicit = run
  }

  return lastExplicit ?? anyRun
}

/**
 * Turns `getShapeTextFlow`'s single frame-wide row pitch into a genuine per-line one, after
 * `layoutShapeTextContent` has already decided which characters land on which row.
 *
 * **The rule (Illustrator/page-layout convention): a line's leading is the distance from ITS OWN
 * baseline to the NEXT line's baseline — never a distance applied to itself.** The very first row
 * never moves — it keeps its original (topBound-anchored) `yTop` unconditionally, independent of any
 * leading value, its own or anyone else's. Every row after that sits at
 * `previousRow.yTop + parsedLeadingPt(previousRow's dominant run)` — i.e. row *i*'s position is
 * governed by row *i-1*'s own resolved leading, not its own. So changing line 2's leading moves line
 * 3 (and everything after), while line 1 and line 2 themselves stay exactly where they were — a
 * partial selection's leading change only ever moves rows strictly AFTER the selection, never the
 * selection itself or anything before it.
 *
 * Each row's own rendered thickness (CSS `lineHeight`/caret height) is deliberately decoupled from
 * its leading — leading is purely an inter-row gap now, not also a stretch on the row's own glyph
 * box — and instead comes from `naturalLeadingPt` of that row's dominant run's point size.
 *
 * A row whose new position no longer fits before the shape's bottom bound (approximated from the
 * last probe row's own extent, since this function only sees the already-computed flow/layout, not
 * the shape) is dropped along with everything after it, shortening `consumedLength` to match.
 */
export function applyPerLineLeadingToFlow(
  flow: ShapeTextFlow,
  layout: ShapeTextLayoutResult,
  charRunMap: Array<TextRun | null>,
  rowMetricPt: RowMetricPt = defaultRowMetricPt
): { flow: ShapeTextFlow; layout: ShapeTextLayoutResult } {
  const charIndicesByOldYTop = new Map<number, number[]>()
  const noteChar = (yTop: number, charIndex: number) => {
    const list = charIndicesByOldYTop.get(yTop)

    if (list) list.push(charIndex)
    else charIndicesByOldYTop.set(yTop, [charIndex])
  }

  for (const chunk of layout.chunks) {
    for (let i = 0; i < chunk.text.length; i++) noteChar(chunk.yTop, chunk.startCharIndex + i)
  }

  for (let i = 0; i < layout.carets.length; i++) {
    noteChar(layout.carets[i].yTop, Math.min(i, charRunMap.length - 1))
  }

  const usedOldYTops = flow.rows.map((r) => r.yTop).filter((y) => charIndicesByOldYTop.has(y))

  if (usedOldYTops.length === 0) return { flow, layout }

  const lastProbeRow = flow.rows[flow.rows.length - 1]
  // The shape's real bottom when the flow carries it. The old fallback — the last probe row's own
  // extent — sits up to a full row high (the row loop stops as soon as the NEXT row would not fit),
  // and on the WASM path `lastProbeRow.rowHeight` was `undefined`, making this `NaN` and the
  // overflow test below silently unreachable in the browser. Kept as the fallback only for a
  // hand-built fixture with no `bottomBound`.
  const approxBottomBound = flow.bottomBound ?? lastProbeRow.yTop + lastProbeRow.rowHeight

  const oldYTopToNewYTop = new Map<number, number>()
  const oldYTopToThickness = new Map<number, number>()
  let previousLeadingRun: TextRun | undefined
  let previousThicknessRun: TextRun | undefined
  let cursorY = 0
  let pendingLeadingPt = 0
  let overflowAtIndex = -1

  for (let rowIdx = 0; rowIdx < usedOldYTops.length; rowIdx++) {
    const oldYTop = usedOldYTops[rowIdx]
    const indices = charIndicesByOldYTop.get(oldYTop) ?? []
    const leadingRun = resolveRowLeadingRun(indices, charRunMap) ?? previousLeadingRun
    const thicknessRun =
      resolveRowTallestRun(indices, charRunMap, rowMetricPt) ?? previousThicknessRun
    // No run at all means an EMPTY frame: `rowMetricPt(undefined)` is the 14pt engine default, which
    // drew the empty caret on a 16.8pt row and snapped it to the real 9.6 on the first keystroke
    // (WMPCM-11367). The flow's own pitch was built from the frame's seeded run, so it is the
    // truthful height for a row that has no character to measure.
    //
    // But that pitch is the LEADING grid (`pointPt * leadingMult`, raised to a typed Leading), so on
    // its own it grows with Leading while the caret ink stays one font tall: the ink box was then
    // centred in a taller row and the caret jumped to the frame's vertical middle once the last
    // character was deleted (WMPCM-11367). A row with text takes its thickness from the run, never
    // from leading, so the empty row asks the measurer for the seeded run's thickness the same way,
    // capped by the pitch so a measurer-less caller still gets the grid's own height.
    const thickness = thicknessRun
      ? rowMetricPt(thicknessRun)
      : Math.min(flow.rowHeight, rowMetricPt(undefined))
    const leadingPointSize = leadingRun?.pointSize ?? DEFAULT_POINT_SIZE
    // Only an EXPLICIT leading overrides the row's own thickness. `resolveRowLeadingRun` falls back
    // to `anyRun` — the LAST run on the row — and `parsedLeadingPt` turns an implicit value into
    // `naturalLeadingPt` of THAT run's point size. So a row ending in 12pt after a 24pt run advanced
    // by 14.4 while occupying 28.8: its glyphs crowded the row below and its selection band
    // overlapped it by half a line, double-compositing the 0.35 highlight into a dark stripe. With
    // no explicit choice to honour, the gap to the next row is this row's own thickness — the same
    // `resolveRowTallestRun` value the glyph box already uses. A uniform row is unaffected
    // (`tallest === last`, so the two expressions agree), which is why this only moves the mixed
    // point-size case it exists to fix.
    //
    // The floor measures the damage bound against the row's OWN ink rather than against the leading
    // run's point size, which is all `parsedLeadingPt` can see from inside. Those differ exactly when
    // the explicit value was authored for a SMALLER run than the row's tallest — a 12pt run's 14.8pt
    // Narrow preset does not describe a choice about the 72pt run beside it, and letting it advance
    // 14.8 under an 86.4pt box is the same overlap in a different channel. Deliberately tight leading
    // is still honoured and still allowed to overlap down to `MIN_LEADING_FRACTION_OF_NATURAL`, which
    // documents itself as a damage bound and NOT a no-overlap guarantee; this only changes what the
    // bound is measured against. A uniform row is unaffected (tallest === leading run).
    const ownLeadingPt =
      leadingRun && hasExplicitLeading(leadingRun.leading)
        ? Math.max(
            parsedLeadingPt(leadingRun.leading, leadingPointSize),
            thickness * MIN_LEADING_FRACTION_OF_NATURAL
          )
        : thickness

    if (rowIdx === 0) {
      cursorY = oldYTop
    } else {
      const oldGap = oldYTop - usedOldYTops[rowIdx - 1]

      // A gap spanning more than one raw grid step means real rows were excluded between these two
      // (e.g. a `jump`/wrap obstacle dropped them) — that gap is real, obstacle-shaped space. Pin
      // `cursorY` to this row's own `oldYTop` rather than adding the gap onto the running `cursorY`:
      // `oldYTop` already IS the correct absolute shape-local position (`applyWrapBandsToShapeTextFlow`
      // pinned it to the obstacle's true bottom edge), while `cursorY` can have drifted away from the
      // raw grid — every ordinary row before this one that packed tighter than the frame's pitch
      // (`pendingLeadingPt < flow.rowHeight`, e.g. a smaller explicit leading) leaves `cursorY` short
      // of `oldYTop` by however much slack accumulated. Adding the gap to that already-short `cursorY`
      // used to land the resumed row short of the obstacle's real bottom by that same accumulated
      // slack — i.e. still inside the obstacle — instead of exactly at it. Only an ordinary one-step
      // gap goes through the per-line-leading repack below, where `pendingLeadingPt` is free to pack
      // tighter or looser than the grid's own pitch.
      cursorY = oldGap > flow.rowHeight + 0.1 ? oldYTop : cursorY + pendingLeadingPt
    }

    if (rowIdx > 0 && cursorY + thickness > approxBottomBound + 0.1) {
      overflowAtIndex = Math.min(...indices)
      break
    }

    oldYTopToNewYTop.set(oldYTop, cursorY)
    oldYTopToThickness.set(oldYTop, thickness)
    pendingLeadingPt = ownLeadingPt
    if (leadingRun) previousLeadingRun = leadingRun
    if (thicknessRun) previousThicknessRun = thicknessRun
  }

  const keepBefore = overflowAtIndex === -1 ? Infinity : overflowAtIndex
  const chunks = layout.chunks
    .filter((c) => c.startCharIndex < keepBefore)
    .map((c) => ({
      ...c,
      yTop: oldYTopToNewYTop.get(c.yTop) ?? c.yTop,
      rowHeight: oldYTopToThickness.get(c.yTop) ?? c.rowHeight,
    }))
  const consumedLength = Math.min(layout.consumedLength, keepBefore)
  const carets = layout.carets.slice(0, consumedLength + 1).map((caret) => ({
    ...caret,
    yTop: oldYTopToNewYTop.get(caret.yTop) ?? caret.yTop,
    rowHeight: oldYTopToThickness.get(caret.yTop) ?? caret.rowHeight,
    nextSlotYTop:
      caret.nextSlotYTop !== undefined
        ? (oldYTopToNewYTop.get(caret.nextSlotYTop) ?? caret.nextSlotYTop)
        : undefined,
  }))
  const rows = flow.rows
    .filter((r) => oldYTopToNewYTop.has(r.yTop))
    .map((r) => ({
      ...r,
      yTop: oldYTopToNewYTop.get(r.yTop) as number,
      baselineY:
        (oldYTopToNewYTop.get(r.yTop) as number) + (oldYTopToThickness.get(r.yTop) as number) * 0.8,
      rowHeight: oldYTopToThickness.get(r.yTop) as number,
    }))

  return {
    flow: { ...flow, rows },
    layout: { chunks, carets, advances: layout.advances, consumedLength },
  }
}
